import React, { useState, useMemo } from "react";
import { Table, Radio, Modal, Typography, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { typeMap, fieldDesc } from "@/constants/dataMap";
import { descMap } from '@/constants/desc';

// ===================== 类型定义 =====================
interface ElementItem {
  mode: string;
  name: string;
  phase: string;
  physical_method: string;
  section: string;
  type: string;
  params: any;
  inputStreams: { name: string; rule: string; streamAttribute: string }[];
  outputStreams: { name: string; rule: string; streamAttribute: string }[];
}

// 流股数据结构
interface StreamItem {
  name: string;
  description: string;
  molar_flow: number;
  mass_flow: number;
  t: number;
  pressure: number;
  materials: {
    molar_flow: number[];
    mole_frac: number[];
  };
}

interface Props {
  design_values: {
    elements: ElementItem[];
    streams: StreamItem[];
    materials: any[];
  };
  highlightedName?: string | null;
  targetElementType?: string | null;
  selectedType?: string;
  onTypeChange?: (type: string) => void;
}

const phaseMap = { VL: "VL-汽液两相", V: "V-汽相", L: "L-液相" };
const modeMap = { TP: "TP-温压闪蒸", VP: "VP-汽化率压力闪蒸", HP: "HP-压焓闪蒸" };

// 格式化数值：去除末尾多余的 0
const formatNumber = (value: any, fixed: number = 2): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return "-";
  }
  // 如果是 0，直接返回 "0"
  if (value === 0) {
    return "0";
  }
  // 保留指定位数小数，然后去除末尾多余的 0
  const fixedValue = Number(value).toFixed(fixed);
  // 去除末尾的 0
  return parseFloat(fixedValue).toString();
};

const ElementsTable: React.FC<Props> = (props) => {
  const { design_values, highlightedName, targetElementType, selectedType: parentSelectedType, onTypeChange } = props;
  const [unit, setUnit] = useState<"mole_frac" | "molar_flow">("mole_frac");
  const elements = design_values.elements || [];
  const allStreams = design_values.streams || [];

  // 监控 design_values 变化
  React.useEffect(() => {
    console.log('design_values 更新:', design_values);
    console.log('allStreams 更新:', allStreams);
  }, [design_values, allStreams]);

  // 设备类型
  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    elements.forEach((item) => set.add(item.type));
    return Array.from(set);
  }, [elements]);

  // 如果有父组件传入的 selectedType，则使用父组件的；否则使用内部状态
  const [internalSelectedType, setInternalSelectedType] = useState<string>(availableTypes[0] || "");
  const selectedType = parentSelectedType !== undefined ? parentSelectedType : internalSelectedType;
  const setSelectedType = onTypeChange || setInternalSelectedType;
  
  // 当 targetElementType 变化时，自动切换到对应类型
  React.useEffect(() => {
    if (targetElementType) {
      setSelectedType(targetElementType);
    }
  }, [targetElementType]);
  
  // 过滤数据：如果 selectedType 为空或"all"，显示所有设备
  const filteredData = useMemo(
    () => {
      if (!selectedType || selectedType === "all") return elements;
      return elements.filter((item) => item.type === selectedType);
    },
    [elements, selectedType],
  );


  // 高亮行样式
  const rowClassName = (record: ElementItem) => {
    return highlightedName === record.name ? 'highlight-row' : '';
  };

  // ===================== 弹窗状态：一次性展示多个流股 =====================
  const [streamModalVisible, setStreamModalVisible] = useState(false);
  const [showStreamList, setShowStreamList] = useState<StreamItem[]>([]);

  // 点击入口流股整列：带出当前设备所有入口流股
  // const handleShowInputStreams = (inputNames: string[]) => {
  //   const list = allStreams.filter(s => inputNames.includes(s.name));
  //   setShowStreamList(list);
  //   setStreamModalVisible(true);
  // };

  // // 点击出口流股整列：带出当前设备所有出口流股
  // const handleShowOutputStreams = (outputNames: string[]) => {
  //   const list = allStreams.filter(s => outputNames.includes(s.name));
  //   setShowStreamList(list);
  //   setStreamModalVisible(true);
  // };
  // 点击入口流股 → 显示所有入口流股（查不到也保留）
  const handleShowInputStreams = (streamNames: string[]) => {
    const list = streamNames.map((name) => {
      const found = allStreams.find((s) => s.name === name);
      return found || { name }; // 查不到也返回 { name }，保证出现在表格
    });
    console.log('打开流股弹窗，当前设计值的流股数据:', allStreams);
    console.log('要显示的流股名称:', streamNames);
    console.log('处理后的流股列表:', list);
    setShowStreamList(list as StreamItem[]);
    setStreamModalVisible(true);
  };

  // 点击出口流股 → 显示所有出口流股（查不到也保留）
  const handleShowOutputStreams = (streamNames: string[]) => {
    const list = streamNames.map((name) => {
      const found = allStreams.find((s) => s.name === name);
      return found || { name }; // 查不到也返回 { name }
    });
    console.log('打开流股弹窗，当前设计值的流股数据:', allStreams);
    console.log('要显示的流股名称:', streamNames);
    console.log('处理后的流股列表:', list);
    setShowStreamList(list as StreamItem[]);
    setStreamModalVisible(true);
  };

  // ===================== 通用列（入口/出口整列可点击，一次性展示全部流股） =====================
  const baseCommonColumns: ColumnsType<ElementItem> = [
    {
      title: "设备名称",
      dataIndex: "name",
      key: "name",
      align: "center",
      width: 110,
    },
    // {
    //   title: fieldDesc.mode,
    //   dataIndex: "mode",
    //   key: "mode",
    //   align: "center",
    //   width: 130,
    //   render: (_, r) => modeMap[r.mode] || r.mode,
    // },
    {
      title: fieldDesc.phase,
      dataIndex: "phase",
      key: "phase",
      align: "center",
      width: 130,
      render: (_, r) => phaseMap[r.phase] || r.phase,
    },
    // {
    //   title: fieldDesc.physical_method,
    //   dataIndex: "physical_method",
    //   align: "center",
    //   width: 130,
    // },
    {
      title: <Typography.Link style={{ cursor: "pointer" }}>入口流股</Typography.Link>,
      key: "inputStreams",
      align: "center",
      width: 200,
      render: (_, r) => {
        const names = r.inputStreams.map((s) => s.name);
        const text = names.join("、") || "-";
        return (
          <Typography.Link onClick={() => handleShowInputStreams(names)}>{text}</Typography.Link>
          // <Tag
          //   color="green"
          //   key={text}
          //   style={{ cursor: 'pointer' }}
          //   onClick={() => handleShowInputStreams(names)}
          // >
          //   {text}
          // </Tag>
        );
      },
    },
    {
      title: <Typography.Link style={{ cursor: "pointer" }}>出口流股</Typography.Link>,
      key: "outputStreams",
      align: "center",
      width: 200,
      render: (_, r) => {
        const names = r.outputStreams.map((s) => s.name);
        const text = names.join("、") || "-";
        return (
          <Typography.Link onClick={() => handleShowOutputStreams(names)}>{text}</Typography.Link>
        );
      },
    },
  ];

  // 【2】混合器 / 分流器 列
  const mixerSplitterColumns = useMemo(
    (): ColumnsType<ElementItem> => [
      ...baseCommonColumns,
      {
        title: fieldDesc.output_f + "（分流比例）",
        key: "output_f",
        align: "center",
        width: 280,
        render: (_, r) => {
          const output_f = r.params?.output_f || [];
          const outputs = r.outputStreams || [];
          return outputs
            .map((s, i) => {
              const val = output_f[i] ?? 0;
              return `${s.name}: ${Number(val).toFixed(2)}`;
            })
            .join(" | ");
        },
      },
    ],
    [],
  );

  // 【3】泵 Pump 列
  const pumpColumns = useMemo(
    (): ColumnsType<ElementItem> => [
      ...baseCommonColumns,
      {
        title: `${fieldDesc.p}（MPa）`,
        key: "p",
        align: "center",
        width: 140,
        render: (_, r) => {
          const p = r.params?.p;
          const unit = r.params?.p_unit || "";
          return p != null ? `${p} ${unit}` : "-";
        },
      },
    ],
    [],
  );

  // ShellAndTubeExchanger 列
  const ShellAndTubeExchangerColumns = useMemo(
    (): ColumnsType<ElementItem> => [
      ...baseCommonColumns,
       {
        title: `${fieldDesc.cold_out_T}（℃）`,
        key: "cold_out_T",
        align: "center",
        width: 280,
        render: (_, r) => {
          const cold_out_T = r.params?.cold_out_T || [];
          const outputs = r.outputStreams || [];
          const unit = r.params?.t_unit || "";
          return outputs
            .map((s, i) => {
              const val = cold_out_T[i] ?? "-";
              return `(${s.name}: ${val})`;
            })
            .join(" | ");
        },
      },
      // {
      //   title: `${fieldDesc.cold_out_P}（MPa）`,
      //   key: "cold_out_P",
      //   align: "center",
      //   width: 140,
      //   render: (_, r) => {
      //     const cold_out_P = r.params?.hot_out_P;
      //     const unit = r.params?.p_unit || "";
      //     return cold_out_P != null ? `${cold_out_P} ${unit}` : "-";
      //   },
      // },
      {
        title: `${fieldDesc.cold_out_P}（MPa）`,
        key: "cold_out_P",
        align: "center",
        width: 280,
        render: (_, r) => {
          const cold_out_P = r.params?.cold_out_P || [];
          const outputs = r.outputStreams || [];
          const unit = r.params?.p_unit || "";
          return outputs
            .map((s, i) => {
              const val = cold_out_P[i] ?? "-";
              return `(${s.name}: ${val})`;
            })
            .join(" | ");
        },
      },
      {
        title: `${fieldDesc.hot_out_T}（℃）`,
        key: "hot_out_T",
        align: "center",
        width: 280,
        render: (_, r) => {
          const hot_out_T = r.params?.hot_out_T || [];
          const outputs = r.outputStreams || [];
          const unit = r.params?.t_unit || "";
          return outputs
            .map((s, i) => {
              const val = hot_out_T[i] ?? "-";
              return `(${s.name}: ${val})`;
            })
            .join(" | ");
        },
      },
      {
        title: `${fieldDesc.hot_out_P}（MPa）`,
        key: "hot_out_P",
        align: "center",
        width: 280,
        render: (_, r) => {
          const hot_out_P = r.params?.hot_out_P || [];
          const outputs = r.outputStreams || [];
          const unit = r.params?.p_unit || "";
          return outputs
            .map((s, i) => {
              const val = hot_out_P[i] ?? "-";
              return `(${s.name}: ${val})`;
            })
            .join(" | ");
        },
      },
    ],
    [],
  );
  const flashColumns = useMemo(
    (): ColumnsType<ElementItem> => [
      ...baseCommonColumns,
      {
        title: `${fieldDesc.t}（℃）`,
        key: "t",
        align: "center",
        width: 140,
        render: (_, r) => {
          const t = r.params?.t;
          const unit = r.params?.t_unit || "";
          return t != null ? `${t} ${unit}` : "-";
        },
      },
      {
        title: `${fieldDesc.p}（MPa）`,
        key: "p",
        align: "center",
        width: 140,
        render: (_, r) => {
          const p = r.params?.p;
          const unit = r.params?.p_unit || "";
          return p != null ? `${p} ${unit}` : "-";
        },
      },
    ],
    [],
  );

  // 【5】精馏塔 DistilStrict 专属表格
  const distilColumns = useMemo(
    (): ColumnsType<ElementItem> => [
      ...baseCommonColumns,
      // {
      //   title: "塔类型",
      //   key: "typeName",
      //   align: "center",
      //   width: 120,
      //   render: (_, r) => r.params?.type || "-",
      // },

      {
        title: fieldDesc.N, // 塔板数
        key: "N",
        align: "center",
        width: 100,
        render: (_, r) => r.params?.N || "-",
      },
      {
        title: `${fieldDesc.P_D}（MPa）`, // 塔顶压力
        key: "P_D",
        align: "center",
        width: 140,
        render: (_, r) => {
          const val = r.params?.P_D;
          return val != null ? val : "-";
        },
      },
      {
        title: fieldDesc.D_col, // 塔径
        key: "D_col",
        align: "center",
        width: 100,
        render: (_, r) => r.params?.D_col || "-",
      },
      {
        title: fieldDesc.C_v, // 孔流系数
        key: "C_v",
        align: "center",
        width: 100,
        render: (_, r) => r.params?.C_v || "-",
      },
      {
        title: fieldDesc.pressure_drop, // 整塔压降
        key: "pressure_drop",
        align: "center",
        width: 100,
        render: (_, r) => r.params?.pressure_drop || "-",
      },
      {
        title: fieldDesc.theta, // 有效孔面积比
        key: "theta",
        align: "center",
        width: 110,
        render: (_, r) => r.params?.theta || "-",
      },
      {
        title: fieldDesc.h_w, // 堤高
        key: "h_w",
        align: "center",
        width: 100,
        render: (_, r) => r.params?.h_w || "-",
      },
      {
        title: fieldDesc.d_hole, // 孔径
        key: "d_hole",
        align: "center",
        width: 100,
        render: (_, r) => r.params?.d_hole || "-",
      },
      // {
      //   title: fieldDesc.trayType || "塔板类型",
      //   key: "trayType",
      //   align: "center",
      //   width: 130,
      //   render: (_, r) => r.params?.trayType || "-",
      // },
      {
        title: fieldDesc.Ns, // 进料位置 → 对应入口流股
        key: "Ns",
        align: "center",
        // width: 220,
        render: (_, r) => {
          const ns = r.params?.Ns || [];
          const inputs = r.inputStreams || [];
          return inputs
            .map((s, i) => {
              const pos = ns[i];
              // 如果位置没有值，显示 "-"
              return pos != null && pos !== "" ? `${s.name}: ${pos} ` : "-";
            })
            .join(", ");
        },
      },
      {
        title: fieldDesc.No, // 出料位置 → 对应出口流股
        key: "No",
        align: "center",
        // width: 220,
        render: (_, r) => {
          const no = r.params?.No || [];
          const outputs = r.outputStreams || [];
          return outputs
            .map((s, i) => {
              const pos = no[i];
              // 如果位置没有值，显示 "-"
              return pos != null && pos !== "" ? `${s.name}: ${pos} ` : "-";
            })
            .join(", ");
        },
      },
      {
        title: fieldDesc.eff, // 塔板效率
        key: "eff",
        align: "center",
        width: 220,
        render: (_, r) => {
          const effList = r.params?.eff || [];
          return (
            effList
              .map((e: any) => {
                return `塔板 ${e.start}-${e.end}：${e.value}`;
              })
              .join(" | ") || "-"
          );
        },
      },
    ],
    [],
  );

  // =====================================================
  // 根据设备类型自动渲染表格
  // =====================================================
  const renderTable = () => {
    switch (selectedType) {
      case "Mixer":
      case "Splitter":
        return (
          <Table
            rowKey="name"
            columns={mixerSplitterColumns}
            dataSource={filteredData}
            pagination={false}
            bordered
            scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
            className="elements-table"
            rowClassName={rowClassName}
          />
        );

      case "Pump":
        return (
          <Table
            rowKey="name"
            columns={pumpColumns}
            dataSource={filteredData}
            pagination={false}
            bordered
            scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
            className="elements-table"
            rowClassName={rowClassName}
          />
        );
        case "ShellAndTubeExchanger":
        return (
          <Table
            rowKey="name"
            columns={ShellAndTubeExchangerColumns}
            dataSource={filteredData}
            pagination={false}
            bordered
            scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
            className="elements-table"
            rowClassName={rowClassName}
          />
        );
        
      case "Flash2":
        return (
          <Table
            rowKey="name"
            columns={flashColumns}
            dataSource={filteredData}
            pagination={false}
            bordered
            scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
            className="elements-table"
            rowClassName={rowClassName}
          />
        );

      case "DistilStrict":
        return (
          <Table
            rowKey="name"
            columns={distilColumns}
            dataSource={filteredData}
            pagination={false}
            bordered
            scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
            className="elements-table"
            rowClassName={rowClassName}
          />
        );

      default:
        return (
          <Table
            rowKey="name"
            columns={baseCommonColumns}
            dataSource={filteredData}
            pagination={false}
            bordered
            scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
            className="elements-table"
            rowClassName={rowClassName}
          />
        );
    }
  };

  return (
    <div className="elements-table-container">
      {renderTable()}

      {/* 多流股统一弹窗 */}
      <Modal
        title="流股详情汇总"
        open={streamModalVisible}
        onCancel={() => {
          setStreamModalVisible(false);
          setShowStreamList([]); // 关闭时清空数据，避免缓存
        }}
        width={1300}
        footer={null}
       zIndex={100002}
      >
        {showStreamList.length > 0 ? (
          <>
            {/* 工艺数据表格 */}
            <Table
              rowKey="name"
              columns={[
                {
                  title: "流股",
                  dataIndex: "name",
                  key: "name",
                  align: "center",
                  width: 100,
                },
                {
                  title: "描述",
                  dataIndex: "name",
                  key: "description",
                  align: "center",
                  width: 200,
                  render: (text, record) => descMap[record.name] || "-",
                },
                {
                  title: "摩尔流量（kmol/h）",
                  key: "molar_flow",
                  align: "center",
                  width: 150,
                  render: (_, r) => formatNumber(r.molar_flow, 3),
                },
                {
                  title: "质量流量（kg/h）",
                  dataIndex: "mass_flow",
                  key: "mass_flow",
                  align: "center",
                  width: 150,
                  render: (v) => formatNumber(v, 2),
                },
                {
                  title: "温度（℃）",
                  dataIndex: "t",
                  key: "t",
                  align: "center",
                  width: 100,
                  render: (v) => formatNumber(v, 2),
                },
                {
                  title: "压力（MPa）",
                  dataIndex: "pressure",
                  key: "pressure",
                  align: "center",
                  width: 100,
                  render: (v) => formatNumber(v, 2),
                },
              ]}
              dataSource={showStreamList}
              pagination={false}
              bordered
              scroll={{ x: "max-content" }}
              size="small"
            />

            <div style={{ marginBottom: 16, marginTop: 16 }}>
              <span style={{ fontWeight: "bold" }}>组分：</span>
              <Radio.Group
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="mole_frac">摩尔分率</Radio.Button>
                <Radio.Button value="molar_flow">摩尔流量 (kmol/h)</Radio.Button>
              </Radio.Group>
            </div>
            {/* 组分表格 */}
            <div style={{ marginTop: 16 }}>
              <Table
                rowKey="name"
                columns={[
                  {
                    title: "流股",
                    dataIndex: "name",
                    key: "name",
                    align: "center",
                    fixed: "left",
                    width: 100,
                  },
                  ...(design_values.materials || []).map((mat, idx) => ({
                    title: mat.name,
                    key: mat.name,
                    align: "center" as const,
                    width: 90,
                    render: (_: any, record: any) => {
                      if (!record.materials) return "-";
                      const { molar_flow, mole_frac } = record.materials;
                      switch (unit) {
                        case "mole_frac":
                          return formatNumber(mole_frac?.[idx], 6);
                        case "molar_flow":
                          return formatNumber(molar_flow?.[idx], 6);
                        default:
                          return "-";
                      }
                    },
                  })),
                ]}
                dataSource={showStreamList}
                pagination={false}
                bordered
                scroll={{ x: "max-content" }}
                size="small"
              />
            </div>
          </>
        ) : (
          <p>未查询到对应流股数据</p>
        )}
      </Modal>
    </div>
  );
};

export default ElementsTable;
